import { Button, Card, Empty, Flex, Image, Pagination, Space, Spin, Tag, Typography } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import BankAccountType from '../BankAccountType/BankAccountType';
import TextCopy from '../../TextCopy';
import { numberFormat } from '../../../helpers/helpers';
import styles from './BankAccountGrid.module.scss';

function renderStateAuth(value) {
    if (value === 'locked') {
        return <Tag color={'red-inverse'}>Bị khóa</Tag>;
    }
    if (value === 'otp') {
        return <Tag color={'orange'}>Xác thực OTP</Tag>;
    }
    if (value === 'password') {
        return <Tag color={'orange'}>Sai mật khẩu</Tag>;
    }
    return <Tag color={'green'}>Bình thường</Tag>;
}

function BankAccountGrid({
    dataSource = [],
    loading = false,
    options = [],
    types = [],
    pagination = {},
    onPaginationChange,
    summaryBalance = 0,
    renderActions,
    onEditPrivate,
    onAddPrivate
}) {
    const isWallet = types.includes('wallet');
    const bankIconMap = options.reduce((acc, option) => {
        acc[option.value] = option.icon;
        return acc;
    }, {});

    return (
        <div className={styles.wrap}>
            <Flex className={styles.header} justify={'space-between'} align={'center'} wrap={'wrap'} gap={8}>
                <Typography.Text>
                    Tổng số dư:{' '}
                    <Tag color={'blue'}>{numberFormat(summaryBalance)}</Tag>
                </Typography.Text>
                <Typography.Text type={'secondary'}>
                    {pagination.total || dataSource.length} tài khoản
                </Typography.Text>
            </Flex>
            <Spin spinning={loading}>
                {dataSource.length === 0 ? (
                    <Empty description={'Không có tài khoản'} />
                ) : (
                    <div className={styles.grid}>
                        {dataSource.map((record) => {
                            const icon = bankIconMap[record.bankName];
                            return (
                                <Card key={record.uuid} hoverable className={styles.card} size={'small'}>
                                    <Flex justify={'space-between'} align={'flex-start'} gap={12}>
                                        <Flex gap={12} align={'center'}>
                                            {icon ? (
                                                <Image src={icon} width={40} preview={false} />
                                            ) : (
                                                <Tag>{record.bankName}</Tag>
                                            )}
                                            <div className={styles.bankTitle}>
                                                <Typography.Text strong>
                                                    {record.displayName || record.bankName}
                                                </Typography.Text>
                                                <Space size={4} wrap>
                                                    <BankAccountType type={record.type} />
                                                    {isWallet && (
                                                        <Tag color={record.level === 5 ? 'green' : 'red'}>
                                                            Cấp {record.level}
                                                        </Tag>
                                                    )}
                                                </Space>
                                            </div>
                                        </Flex>
                                        {renderStateAuth(record.stateAuth)}
                                    </Flex>

                                    <div className={styles.account}>
                                        <Typography.Text>{record.accountName}</Typography.Text>
                                        <TextCopy value={record.accountNumber} />
                                        <Typography.Text type={'secondary'}>
                                            {record.username}
                                        </Typography.Text>
                                    </div>

                                    <div className={styles.balance}>
                                        <Typography.Text type={'secondary'}>Số dư</Typography.Text>
                                        <Typography.Title level={4} style={{ margin: 0 }}>
                                            {numberFormat(record.balance)}
                                        </Typography.Title>
                                    </div>

                                    <div className={styles.meta}>
                                        <div>
                                            <span className={styles.metaLabel}>Nạp ngày</span>
                                            <Typography.Text>
                                                {numberFormat(record.dailyReceiveAmount || 0)} / {numberFormat(record.maxAmountLimitPerDay || 0)}
                                            </Typography.Text>
                                        </div>
                                        <div>
                                            <span className={styles.metaLabel}>Chuyển ngày</span>
                                            <Typography.Text>
                                                {numberFormat(record.dailyTransferAmount || 0)} / {numberFormat(record.maxAmountLimitTransferPerDay || 0)}
                                            </Typography.Text>
                                        </div>
                                    </div>

                                    <Space className={styles.tags} size={[4, 4]} wrap>
                                        {record.status === true ? (
                                            <Tag color={'green'}>OKE</Tag>
                                        ) : (
                                            <Tag color={'red'}>Lỗi</Tag>
                                        )}
                                        {record.state === 'disabled' ? (
                                            <Tag color={'red'}>Không hoạt động</Tag>
                                        ) : (
                                            <Tag color={'green'}>Hoạt động</Tag>
                                        )}
                                        {record.autoReLoginEnabled ? (
                                            <Tag color={'green'}>Tự đăng nhập</Tag>
                                        ) : (
                                            <Tag color={'red'}>Tắt tự đăng nhập</Tag>
                                        )}
                                        {!isWallet && record.bankAccountPrivates?.length === 0 && (
                                            <Tag>ALL</Tag>
                                        )}
                                        {!isWallet && record.bankAccountPrivates?.map((item, index) => (
                                            <Tag color={'orange'} key={index}>{item.username}</Tag>
                                        ))}
                                        {!isWallet && (
                                            <>
                                                <Button
                                                    type={'primary'}
                                                    size={'small'}
                                                    icon={<EditOutlined />}
                                                    title={'Sửa đối tác'}
                                                    onClick={() => onEditPrivate?.(record)}
                                                />
                                                <Button
                                                    size={'small'}
                                                    icon={<PlusOutlined />}
                                                    title={'Thêm đối tác'}
                                                    onClick={() => onAddPrivate?.(record)}
                                                />
                                            </>
                                        )}
                                    </Space>

                                    {typeof renderActions === 'function' && (
                                        <div className={styles.actions}>
                                            {renderActions(record)}
                                        </div>
                                    )}
                                </Card>
                            );
                        })}
                    </div>
                )}
            </Spin>
            {pagination.total > 0 && (
                <div className={styles.pagination}>
                    <Pagination
                        current={pagination.current}
                        pageSize={pagination.pageSize}
                        total={pagination.total}
                        showSizeChanger
                        showTotal={(total) => `${total} tài khoản`}
                        onChange={onPaginationChange}
                    />
                </div>
            )}
        </div>
    );
}

export default BankAccountGrid;
